import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Avatar,
  Tabs,
  Tab
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import {
  SearchOutlined as SearchOutlinedIcon,
  ArrowForwardOutlined as ArrowForwardOutlinedIcon,
  AccessTimeOutlined as AccessTimeOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  RefreshOutlined as RefreshOutlinedIcon,
  BusinessOutlined as BusinessOutlinedIcon,
  StarOutlined as StarOutlinedIcon,
  TrendingUpOutlined as TrendingUpOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';
import axios from 'axios';
import AppService from '../../../Configurations/Appservice';
import { API_ENDPOINTS } from '../../../Api/endpoints';
import { addAuthHeader } from '../../../utils/authUtils';
import { STAT_FIELDS, DOCUMENT_TYPE_MAPPING } from '../../../Configurations/StatFieldData';
import { useDispatch } from 'react-redux';
import { showErroMessage } from '../../../LayoutContainers/AppLayout/redux/AppSlice';

const STEPS = ['Select Service', 'Fill Fields', 'Review & Submit'];

// Brand color
const PRIMARY = '#064d51';

/**
 * Get auth info from localStorage
 */
const getAuthInfo = () => {
  try {
    const raw = localStorage.getItem('authInfo');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Generate field name variants for flexible matching (mirrors NewTicket pattern)
 */
const generateFieldNameVariants = (docTypeCode) => {
  if (!docTypeCode) return [];
  const code = docTypeCode.toLowerCase();
  const camelCase = DOCUMENT_TYPE_MAPPING[code] || DOCUMENT_TYPE_MAPPING[docTypeCode];
  const variants = [code, docTypeCode];
  if (camelCase) variants.push(camelCase);
  const withoutCopy = code.replace(/_copy$/, '');
  if (withoutCopy !== code) variants.push(withoutCopy);
  return [...new Set(variants)];
};

/**
 * OrgTicketRequest — Organization ticket creation form.
 *
 * Step 0: Select a service (fetched from API, filtered to ORGANIZATION target type)
 * Step 1: Fill fields (auto-populated from org profile + documents)
 * Step 2: Review & Submit
 */
const OrgTicketRequest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceId = searchParams.get('serviceId');

  // Stepper — start at step 1 if serviceId already in URL
  const [activeStep, setActiveStep] = useState(serviceId ? 1 : 0);

  // Loading states
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadingFields, setLoadingFields] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Step 0: Service selection
  const [services, setServices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState(serviceId || null);
  const [selectedCategory, setSelectedCategory] = useState('ALL');

  // Step 1: Service fields
  const [service, setService] = useState(null);
  const [serviceFields, setServiceFields] = useState([]);
  const [staticFieldCodes, setStaticFieldCodes] = useState([]);

  // Org data for auto-fill
  const [orgProfile, setOrgProfile] = useState(null);
  const [orgDocGroups, setOrgDocGroups] = useState([]);

  // Form values
  const [dynamicValues, setDynamicValues] = useState({});
  const [staticValues, setStaticValues] = useState({});
  const [fileMetadata, setFileMetadata] = useState({});
  const [orgDocumentReferences, setOrgDocumentReferences] = useState([]);
  // All org document types (for mapping field code → documentTypeId during upload)
  const [allDocTypes, setAllDocTypes] = useState([]);

  const authInfo = useMemo(() => getAuthInfo(), []);
  const orgId = authInfo?.principalInfo?.organisationId
    || authInfo?.user?.organisation?.id
    || localStorage.getItem('organizationId');

  // --- Step 0: Load available services + all document types ---
  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoadingServices(true);
        const [servicesRes, docTypesRes] = await Promise.allSettled([
          AppService.get('v1/serviceMapping/mappedServices', { params: { targetType: 'ORGANIZATION' } }),
          axios.get(API_ENDPOINTS.ORGANIZATION_DOCUMENT_TYPES.LIST, { headers: addAuthHeader() }),
        ]);
        setServices(servicesRes.status === 'fulfilled' && Array.isArray(servicesRes.value) ? servicesRes.value : []);
        if (docTypesRes.status === 'fulfilled' && docTypesRes.value?.data) {
          setAllDocTypes(Array.isArray(docTypesRes.value.data) ? docTypesRes.value.data : []);
        }
      } catch (err) {
        console.error('Failed to load services:', err);
        setServices([]);
      } finally {
        setLoadingServices(false);
      }
    };
    loadServices();
  }, []);

  // --- Step 1: Load service fields + org data when a service is selected ---
  const loadServiceData = useCallback(async (svcId) => {
    if (!svcId || !orgId) return;

    try {
      setLoadingFields(true);
      setError(null);

      const [serviceFieldsRes, orgDocsRes, orgProfileRes] = await Promise.allSettled([
        AppService.get('v1/serviceField/byService', { params: { serviceId: svcId } }),
        axios.get(API_ENDPOINTS.ORGANIZATION_DOCUMENTS.GROUPED(orgId), { headers: addAuthHeader() }),
        axios.get(API_ENDPOINTS.ORG_DETAILS.BY_ORG(orgId), { headers: addAuthHeader() }).catch(() => null),
      ]);

      // Process service fields
      if (serviceFieldsRes.status === 'fulfilled' && serviceFieldsRes.value) {
        const res = serviceFieldsRes.value;
        console.log('Service fields API response:', res);
        console.log('serviceFields:', res.serviceFields);
        console.log('staticFields:', res.staticFields);
        setServiceFields(res.serviceFields || []);
        setStaticFieldCodes(res.staticFields || []);
        if (res.service) setService(res.service);
      } else {
        console.error('Service fields API failed:', serviceFieldsRes);
        setError('Failed to load service fields.');
        return;
      }

      // Process org documents — API returns { groups: [...], summary: {...} }
      let docGroups = [];
      if (orgDocsRes.status === 'fulfilled' && orgDocsRes.value?.data) {
        const raw = orgDocsRes.value.data;
        docGroups = Array.isArray(raw) ? raw : (Array.isArray(raw.groups) ? raw.groups : []);
        setOrgDocGroups(docGroups);
      }

      // Process org profile
      let profile = null;
      if (orgProfileRes.status === 'fulfilled' && orgProfileRes.value?.data) {
        profile = orgProfileRes.value.data;
        setOrgProfile(profile);
      }

      // Auto-populate
      autoPopulateFromOrgData(
        serviceFieldsRes.status === 'fulfilled' ? serviceFieldsRes.value?.staticFields || [] : [],
        profile,
        docGroups
      );
    } catch (err) {
      console.error('Error loading service data:', err);
      setError('Failed to load service data.');
    } finally {
      setLoadingFields(false);
    }
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // If serviceId is in URL on mount, load its data
  useEffect(() => {
    if (serviceId) {
      loadServiceData(serviceId);
    }
  }, [serviceId, loadServiceData]);

  /**
   * Auto-populate static field values from org profile and grouped documents.
   */
  const autoPopulateFromOrgData = useCallback((staticCodes, profile, docGroups) => {
    const newStaticValues = {};
    const newFileMetadata = {};
    const newDocRefs = [];
    const populatedFields = new Set();

    // 1. Auto-fill from org profile (text/date fields)
    if (profile) {
      for (const code of staticCodes) {
        if (profile[code] !== undefined && profile[code] !== null && profile[code] !== '') {
          newStaticValues[code] = String(profile[code]);
          populatedFields.add(code);
        }
      }
    }

    // 2. Auto-fill from org document groups (FILE fields + metadata)
    if (docGroups && docGroups.length > 0) {
      for (const group of docGroups) {
        // API shape: { documentGroupId, common: { documentTypeCode, isCurrent, ... }, documents: [...] }
        const common = group.common || {};
        if (common.isCurrent === false) continue;

        const docTypeCode = common.documentTypeCode || common.documentTypeName || '';
        const variants = generateFieldNameVariants(docTypeCode);

        let matchedFieldCode = null;
        for (const variant of variants) {
          if (staticCodes.includes(variant)) {
            matchedFieldCode = variant;
            break;
          }
        }

        if (!matchedFieldCode) {
          const mapped = DOCUMENT_TYPE_MAPPING[docTypeCode] || DOCUMENT_TYPE_MAPPING[docTypeCode.toLowerCase()];
          if (mapped && staticCodes.includes(mapped)) {
            matchedFieldCode = mapped;
          }
        }

        if (!matchedFieldCode) continue;

        const fieldDef = STAT_FIELDS[matchedFieldCode];

        if (fieldDef && fieldDef.type === 'FILE') {
          const primaryDoc = group.documents?.find(d => d.isPrimary) || group.documents?.[0];
          if (primaryDoc) {
            newFileMetadata[matchedFieldCode] = {
              filePath: primaryDoc.filePath,
              fileName: primaryDoc.fileName,
              documentGroupId: group.documentGroupId || primaryDoc.documentGroupId,
            };
            newStaticValues[matchedFieldCode] = primaryDoc.filePath;
            populatedFields.add(matchedFieldCode);

            newDocRefs.push({
              documentGroupId: (group.documentGroupId || primaryDoc.documentGroupId)?.toString(),
              documentType: docTypeCode,
            });
          }
        }

        if (common.documentNumber) {
          const numberFieldMap = {
            'TRADE_LICENSE': 'licenseNumber',
            'ESTABLISHMENT_CARD': 'establishmentCardNumber',
            'IMMIGRATION_CARD': 'immigrationCardNumber',
            'TRN_CERTIFICATE': 'trnNumber',
            'VAT_CERTIFICATE': 'vatRegistrationNumber',
            'CHAMBER_CERTIFICATE': 'chamberMembershipNumber',
            'MOHRE_LICENSE': 'mohreLicenseNumber',
          };
          const numberField = numberFieldMap[docTypeCode];
          if (numberField && staticCodes.includes(numberField) && !populatedFields.has(numberField)) {
            newStaticValues[numberField] = common.documentNumber;
            populatedFields.add(numberField);
          }
        }
      }
    }

    setStaticValues(prev => ({ ...prev, ...newStaticValues }));
    setFileMetadata(prev => ({ ...prev, ...newFileMetadata }));
    setOrgDocumentReferences(newDocRefs);
  }, []);

  // --- Handlers ---

  const handleSelectService = async (svc) => {
    setSelectedServiceId(svc.id);
    setSearchParams({ serviceId: svc.id });
    // Reset form state for new service
    setDynamicValues({});
    setStaticValues({});
    setFileMetadata({});
    setOrgDocumentReferences([]);
    setServiceFields([]);
    setStaticFieldCodes([]);
    // Load fields and advance to step 1
    await loadServiceData(svc.id);
    setActiveStep(1);
  };

  const handleDynamicChange = (fieldId, value) => {
    setDynamicValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleStaticChange = (code, value) => {
    setStaticValues(prev => ({ ...prev, [code]: value }));
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0: return !!selectedServiceId;
      case 1: return true;
      case 2: return true;
      default: return false;
    }
  };

  /**
   * Find the org document type ID for a static field code.
   * Maps field code (e.g. tradeLicenseCopy) → DOCUMENT_TYPE_MAPPING → doc type code → allDocTypes.
   */
  const findDocTypeIdForField = (fieldCode) => {
    // Get the snake_case doc type code from mapping (e.g. tradeLicenseCopy → trade_license)
    const mappedCode = DOCUMENT_TYPE_MAPPING[fieldCode];
    if (!mappedCode) return null;

    // Try to match against allDocTypes by code (flexible: case-insensitive, with/without underscores)
    const normalize = (s) => (s || '').toLowerCase().replace(/[_\s-]/g, '');
    const target = normalize(mappedCode);

    const match = allDocTypes.find(dt => {
      const dtCode = normalize(dt.code);
      return dtCode === target || target.includes(dtCode) || dtCode.includes(target);
    });

    return match || null;
  };

  // File upload for FILE-type static fields — uploads via org document bulk API
  const [uploadingField, setUploadingField] = useState(null);
  const handleFileUpload = async (code, file) => {
    if (!file || !orgId) return;
    setUploadingField(code);
    try {
      // Step 1: Upload file to S3
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', 'ORGANISATION_DOCUMENT');
      formData.append('fileName', file.name);

      const uploadRes = await axios.post(API_ENDPOINTS.FILES.UPLOAD, formData, {
        headers: { ...addAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      const filePath = uploadRes.data?.data?.filePath || uploadRes.data?.filePath;
      if (!filePath) throw new Error('No file path returned from upload');

      // Step 2: Find the matching document type
      const docType = findDocTypeIdForField(code);

      if (docType) {
        // Step 3: Save as org document via bulk API (creates TOrganizationDocument with documentGroupId)
        const bulkRequest = {
          documentTypeId: docType.id,
          replaceExisting: false,
          sharedMetadata: {},
          documents: [{ filePath, fileName: file.name, contentType: file.type, fileSize: file.size }],
        };

        const bulkRes = await axios.post(
          API_ENDPOINTS.ORGANIZATION_DOCUMENTS.BULK(orgId),
          bulkRequest,
          { headers: addAuthHeader() }
        );

        // Extract documentGroupId from bulk response
        const savedDocs = Array.isArray(bulkRes.data) ? bulkRes.data : [bulkRes.data];
        const savedDoc = savedDocs[0];
        const documentGroupId = savedDoc?.documentGroupId;

        setStaticValues(prev => ({ ...prev, [code]: filePath }));
        setFileMetadata(prev => ({
          ...prev,
          [code]: { filePath, fileName: file.name, documentGroupId }
        }));

        // Add to orgDocumentReferences if we got a documentGroupId
        if (documentGroupId) {
          setOrgDocumentReferences(prev => [
            ...prev.filter(r => r.documentType !== docType.code),
            { documentGroupId: documentGroupId.toString(), documentType: docType.code }
          ]);
        }
      } else {
        // No matching doc type found — fall back to just storing file path
        console.warn(`No matching org document type found for field "${code}", storing file path only`);
        setStaticValues(prev => ({ ...prev, [code]: filePath }));
        setFileMetadata(prev => ({
          ...prev,
          [code]: { filePath, fileName: file.name, documentGroupId: null }
        }));
      }
    } catch (err) {
      console.error('File upload failed:', err);
      dispatch(showErroMessage('Failed to upload file'));
    } finally {
      setUploadingField(null);
    }
  };

  const handleFileRemove = (code) => {
    // Also remove from orgDocumentReferences if it had a documentGroupId
    const meta = fileMetadata[code];
    if (meta?.documentGroupId) {
      setOrgDocumentReferences(prev => prev.filter(r => r.documentGroupId !== meta.documentGroupId.toString()));
    }
    setStaticValues(prev => { const n = { ...prev }; delete n[code]; return n; });
    setFileMetadata(prev => { const n = { ...prev }; delete n[code]; return n; });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        serviceId: selectedServiceId,
        ticketTargetType: 'ORGANIZATION',
        fields: dynamicValues,
        orgDocumentReferences,
        ...staticValues,
      };

      await AppService.post('v1/tickets', payload);
      navigate('/CST-DocServices/requests');
    } catch (err) {
      console.error('Failed to create ticket:', err);
      dispatch(showErroMessage(err?.response?.data?.message || 'Failed to create ticket'));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Field Renderers ---

  const renderStaticField = (code) => {
    const fieldDef = STAT_FIELDS[code];
    if (!fieldDef) {
      return (
        <TextField key={code} label={code} value={staticValues[code] || ''}
          onChange={(e) => handleStaticChange(code, e.target.value)} fullWidth size="small" />
      );
    }

    if (fieldDef.type === 'FILE') {
      const meta = fileMetadata[code];
      const isUploading = uploadingField === code;
      return (
        <Box key={code}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {fieldDef.label}
          </Typography>
          {meta ? (
            <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsertDriveFileOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                {meta.fileName || 'Document attached'}
              </Typography>
              {meta.filePath && (
                <Tooltip title="View document">
                  <IconButton size="small" href={API_ENDPOINTS.FILES.DOWNLOAD(meta.filePath)} target="_blank">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => handleFileRemove(code)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              component="label"
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: isUploading ? 'default' : 'pointer',
                borderStyle: 'dashed',
                borderColor: 'divider',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              {isUploading ? (
                <CircularProgress size={20} />
              ) : (
                <CloudUploadOutlinedIcon color="action" fontSize="small" />
              )}
              <Typography variant="body2" color="text.secondary">
                {isUploading ? 'Uploading...' : 'Click to upload'}
              </Typography>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                hidden
                disabled={isUploading}
                onChange={(e) => {
                  if (e.target.files?.[0]) handleFileUpload(code, e.target.files[0]);
                  e.target.value = '';
                }}
              />
            </Paper>
          )}
        </Box>
      );
    }

    const typeMap = { DATE: 'date', EMAIL: 'email', PHONE: 'tel' };
    const inputType = typeMap[fieldDef.type] || 'text';

    return (
      <TextField key={code} type={inputType} label={fieldDef.label}
        value={staticValues[code] || ''} onChange={(e) => handleStaticChange(code, e.target.value)}
        fullWidth size="small" InputLabelProps={fieldDef.type === 'DATE' ? { shrink: true } : undefined} />
    );
  };

  const renderDynamicField = (field) => {
    const fieldId = field.id;
    const label = field.title || field.label || field.name || field.code || 'Field';
    const type = (field.type || 'TEXT').toUpperCase();

    return (
      <TextField key={fieldId} type={type === 'DATE' ? 'date' : 'text'} label={label}
        value={dynamicValues[fieldId] || ''} onChange={(e) => handleDynamicChange(fieldId, e.target.value)}
        fullWidth size="small" required={field.mandatory}
        multiline={type === 'TEXTAREA'} rows={type === 'TEXTAREA' ? 3 : 1}
        InputLabelProps={type === 'DATE' ? { shrink: true } : undefined} />
    );
  };

  // --- Step 0: Service Selection ---

  // Group services by serviceGroupId/groupName
  const GROUP_COLORS = ['#E91E63', '#3F51B5', '#673AB7', '#00BCD4', '#FF9800', '#607D8B', '#4CAF50', '#795548'];
  const groupedServices = useMemo(() => {
    const groupMap = {};
    services.forEach((svc) => {
      const gId = svc.serviceGroupId || 'ungrouped';
      if (!groupMap[gId]) {
        const colorIndex = Object.keys(groupMap).length % GROUP_COLORS.length;
        groupMap[gId] = { id: gId, name: svc.groupName || 'Other', color: GROUP_COLORS[colorIndex], services: [] };
      }
      groupMap[gId].services.push(svc);
    });
    return Object.values(groupMap);
  }, [services]);

  const categories = useMemo(() => {
    const allCount = services.length;
    return [
      { id: 'ALL', code: 'ALL', name: 'All Services', color: '#666', count: allCount },
      ...groupedServices.map(g => ({ id: g.id, code: g.id, name: g.name, color: g.color, count: g.services.length }))
    ];
  }, [groupedServices, services.length]);

  const filteredServices = useMemo(() => {
    let filtered = selectedCategory === 'ALL' ? groupedServices : groupedServices.filter(g => g.id === selectedCategory);
    if (searchQuery) {
      filtered = filtered.map(group => ({
        ...group,
        services: group.services.filter(svc =>
          (svc.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (svc.description || '').toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(group => group.services.length > 0);
    }
    return filtered;
  }, [groupedServices, selectedCategory, searchQuery]);

  const totalFilteredCount = useMemo(() => filteredServices.reduce((acc, g) => acc + g.services.length, 0), [filteredServices]);

  // Service card renderer (matches DocumentServicesPage)
  const renderServiceCard = (svc, group) => {
    const totalCost = (Number(svc.govtCharge) || 0) + (Number(svc.serviceCharge) || 0);
    return (
      <Card
        sx={{
          height: '100%', display: 'flex', flexDirection: 'column',
          transition: 'all 0.2s', position: 'relative',
          border: '1px solid #e0e0e0', borderRadius: 2,
          '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)', transform: 'translateY(-2px)', borderColor: PRIMARY }
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 1.5, pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <Avatar sx={{ width: 36, height: 36, backgroundColor: `${group.color}15`, flexShrink: 0 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20, color: group.color }} />
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#212121', lineHeight: 1.3,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {svc.name}
              </Typography>
              {group.name && (
                <Tooltip title={`Uses "${group.name}" form`} arrow placement="top">
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mt: 0.25, cursor: 'help' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: group.color }} />
                    <Typography sx={{ fontSize: 9, color: '#9e9e9e' }}>{group.name} form</Typography>
                    <InfoOutlinedIcon sx={{ fontSize: 10, color: '#bdbdbd' }} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 11, color: '#757575', lineHeight: 1.4, mb: 1, minHeight: 30,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {svc.description || ''}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            py: 0.75, px: 1, borderRadius: 1, backgroundColor: '#fafafa', mb: 1 }}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: PRIMARY }}>
                AED {totalCost.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: 9, color: '#9e9e9e' }}>
                G: {Number(svc.govtCharge || 0).toFixed(0)} + S: {Number(svc.serviceCharge || 0).toFixed(0)}
              </Typography>
            </Box>
            <Tooltip title={`Estimated ${svc.estimatedDays || 'N/A'} business days`} arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <AccessTimeOutlinedIcon sx={{ fontSize: 12, color: '#9e9e9e' }} />
                <Typography sx={{ fontSize: 10, color: '#757575', fontWeight: 500 }}>
                  {svc.estimatedDays || '—'}d
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </CardContent>
        <Box sx={{ p: 1.5, pt: 0 }}>
          <Button variant="contained" fullWidth size="small"
            endIcon={<ArrowForwardOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => handleSelectService(svc)}
            sx={{ py: 0.75, fontSize: 12, fontWeight: 600, backgroundColor: PRIMARY, '&:hover': { backgroundColor: '#00796b' } }}>
            Request
          </Button>
        </Box>
      </Card>
    );
  };

  const renderStep0 = () => {
    if (loadingServices) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (services.length === 0) {
      return (
        <Alert severity="info">
          No organization services are available for your account. Please contact your administrator.
        </Alert>
      );
    }

    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, backgroundColor: `${PRIMARY}15` }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20, color: PRIMARY }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#212121' }}>
                Document Services
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#757575' }}>
                License renewals, Government cards & more
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={() => {
                setLoadingServices(true);
                AppService.get('v1/serviceMapping/mappedServices', { params: { targetType: 'ORGANIZATION' } })
                  .then(res => setServices(Array.isArray(res) ? res : []))
                  .catch(() => setServices([]))
                  .finally(() => setLoadingServices(false));
              }} size="small" sx={{ color: '#757575' }}>
                <RefreshOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" size="small"
              startIcon={<BusinessOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/CST-DocServices/requests')}
              sx={{ fontSize: 12, px: 1.5, py: 0.5, borderColor: PRIMARY, color: PRIMARY,
                '&:hover': { borderColor: PRIMARY, backgroundColor: `${PRIMARY}10` } }}>
              My Requests
            </Button>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField size="small" placeholder="Search services..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: (
              <InputAdornment position="start"><SearchOutlinedIcon sx={{ fontSize: 18, color: '#9e9e9e' }} /></InputAdornment>
            ) }}
            sx={{ width: 300, backgroundColor: '#fff',
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#e0e0e0' },
                '&:hover fieldset': { borderColor: PRIMARY },
                '&.Mui-focused fieldset': { borderColor: PRIMARY },
              },
              '& .MuiInputBase-input': { py: 1, fontSize: 13 }
            }}
          />
        </Box>

        {/* Popular Services — show top 4 when no filters */}
        {!searchQuery && selectedCategory === 'ALL' && groupedServices.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
              <StarOutlinedIcon sx={{ fontSize: 18, color: '#ffa000' }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#424242' }}>Popular Services</Typography>
            </Box>
            <Grid container spacing={2}>
              {groupedServices
                .flatMap(g => g.services.map(s => ({ ...s, _group: g })))
                .slice(0, 4)
                .map(svc => (
                  <Grid item xs={12} sm={6} md={3} key={`pop-${svc.id}`}>
                    {renderServiceCard(svc, svc._group)}
                  </Grid>
                ))}
            </Grid>
            <Divider sx={{ mt: 3 }} />
          </Box>
        )}

        {/* Category Tabs */}
        <Card sx={{ mb: 2, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={selectedCategory} onChange={(e, v) => setSelectedCategory(v)}
              variant="scrollable" scrollButtons="auto"
              sx={{ px: 1, minHeight: 40,
                '& .MuiTabs-indicator': { backgroundColor: PRIMARY, height: 2 },
                '& .MuiTab-root': { minHeight: 40, py: 0.5, px: 1.5, fontSize: 12, fontWeight: 500, color: '#757575',
                  '&.Mui-selected': { color: PRIMARY, fontWeight: 600 } }
              }}>
              {categories.map((cat) => (
                <Tab key={cat.code} value={cat.code} label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {cat.code !== 'ALL' && (
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cat.color }} />
                    )}
                    <span>{cat.name}</span>
                    <Chip label={cat.count} size="small"
                      sx={{ height: 16, fontSize: 10, fontWeight: 600, '& .MuiChip-label': { px: 0.5 },
                        bgcolor: selectedCategory === cat.code ? PRIMARY : '#e0e0e0',
                        color: selectedCategory === cat.code ? '#fff' : '#757575' }} />
                  </Box>
                } />
              ))}
            </Tabs>
          </Box>

          {/* Services Grid grouped by service group */}
          <Box sx={{ p: 2 }}>
            {filteredServices.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? `No services match "${searchQuery}".` : 'No services available in this category.'}
                </Typography>
                {searchQuery && (
                  <Button size="small" onClick={() => setSearchQuery('')} sx={{ mt: 1, color: PRIMARY }}>
                    Clear Search
                  </Button>
                )}
              </Box>
            ) : (
              <>
                {filteredServices.map((group) => (
                  <Box key={group.id} sx={{ mb: 3 }}>
                    {selectedCategory === 'ALL' && (
                      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: group.color }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#424242' }}>{group.name}</Typography>
                        <Chip label={group.services.length} size="small"
                          sx={{ height: 18, fontSize: 10, fontWeight: 600,
                            backgroundColor: `${group.color}20`, color: group.color }} />
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      {group.services.map((svc) => (
                        <Grid item xs={12} sm={6} md={3} key={svc.id}>
                          {renderServiceCard(svc, group)}
                        </Grid>
                      ))}
                    </Grid>
                    {selectedCategory === 'ALL' && <Divider sx={{ mt: 3 }} />}
                  </Box>
                ))}
                <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>
                    Showing {totalFilteredCount} services{searchQuery && ` matching "${searchQuery}"`}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Card>
      </Box>
    );
  };

  // --- Step 1: Fill Fields ---

  const renderStep1 = () => {
    if (loadingFields) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const hasStaticFields = staticFieldCodes.length > 0;
    const hasDynamicFields = serviceFields.length > 0;

    if (!hasStaticFields && !hasDynamicFields) {
      return (
        <Alert severity="info">
          This service does not require any additional fields. You can proceed to review.
        </Alert>
      );
    }

    return (
      <Box>
        {hasStaticFields && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
              Organization Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fields have been auto-filled from your organization profile and documents. You can edit any value.
            </Typography>
            <Grid container spacing={2}>
              {staticFieldCodes.map((code) => (
                <Grid item xs={12} sm={6} key={code}>
                  {renderStaticField(code)}
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {hasDynamicFields && (
          <Box>
            {hasStaticFields && <Divider sx={{ my: 3 }} />}
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
              Service-Specific Fields
            </Typography>
            <Grid container spacing={2}>
              {serviceFields.map((field) => (
                <Grid item xs={12} sm={6} key={field.id}>
                  {renderDynamicField(field)}
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  // --- Step 2: Review ---

  const renderStep2 = () => {
    const selectedSvc = services.find(s => s.id === selectedServiceId) || service;

    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          Review Your Request
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Service</Typography>
          <Typography variant="body1">{selectedSvc?.name || selectedServiceId}</Typography>
        </Paper>

        {staticFieldCodes.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Organization Details
            </Typography>
            <Grid container spacing={1}>
              {staticFieldCodes.map((code) => {
                const fieldDef = STAT_FIELDS[code];
                const value = staticValues[code];
                const meta = fileMetadata[code];
                if (!value && !meta) return null;
                return (
                  <Grid item xs={12} sm={6} key={code}>
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {fieldDef?.label || code}
                      </Typography>
                      {fieldDef?.type === 'FILE' && meta ? (
                        <Typography variant="body2">{meta.fileName || 'Document attached'}</Typography>
                      ) : (
                        <Typography variant="body2">{value}</Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}

        {serviceFields.length > 0 && Object.keys(dynamicValues).some(k => dynamicValues[k]) && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Service Details
            </Typography>
            <Grid container spacing={1}>
              {serviceFields.map((field) => {
                const value = dynamicValues[field.id];
                if (!value) return null;
                return (
                  <Grid item xs={12} sm={6} key={field.id}>
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {field.title || field.label || field.name}
                      </Typography>
                      <Typography variant="body2">{value}</Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}

        {orgDocumentReferences.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Attached Documents ({orgDocumentReferences.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {orgDocumentReferences.map((ref, i) => (
                <Chip key={i} icon={<InsertDriveFileOutlinedIcon />}
                  label={ref.documentType || 'Document'} size="small" variant="outlined" />
              ))}
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  // --- Main Render ---

  if (error && !selectedServiceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => { setError(null); setActiveStep(0); }}>
          Back to Services
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header — only show on steps 1 and 2 */}
      {activeStep > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={() => {
            if (activeStep === 1) {
              setSelectedServiceId(null);
              setSearchParams({});
              setActiveStep(0);
            } else {
              setActiveStep(prev => prev - 1);
            }
          }} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="medium">
            New Service Request
          </Typography>
        </Box>
      )}

      {/* Stepper — only show on steps 1 and 2 */}
      {activeStep > 0 && (
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {/* Step Content */}
      {activeStep === 0 ? (
        renderStep0()
      ) : (
        <Card sx={{ border: '1px solid #e0e0e0', boxShadow: 'none' }}>
          <CardContent sx={{ p: 3 }}>
            {activeStep === 1 && renderStep1()}
            {activeStep === 2 && renderStep2()}
          </CardContent>
        </Card>
      )}

      {/* Navigation — only show for steps 1 and 2 */}
      {activeStep > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            disabled={submitting}
            onClick={() => {
              if (activeStep === 1) {
                setSelectedServiceId(null);
                setSearchParams({});
                setActiveStep(0);
              } else {
                setActiveStep(prev => prev - 1);
              }
            }}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeStep < STEPS.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setActiveStep(prev => prev + 1)}
                disabled={!isStepValid(activeStep) || loadingFields}
                sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#053d40' } }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                onClick={handleSubmit}
                disabled={submitting}
                sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#053d40' } }}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OrgTicketRequest;
